import URI from 'jsuri';

import config from 'js/app/config';

import {
  HelpCenters,
  buildCommunityFallbackUrl,
  buildCommunityFeedUrl,
  buildCommunityGroupUrl,
  buildSalesforceArticleUrl,
  buildSalesforceFallbackUrl,
  buildSalesforceHelpCenterHomeUrl,
  buildSalesforceUrl,
  buildSalesforceWebformUrl,
} from 'bundles/common/utils/salesforceUrlBuilder';
import type { HelpCenterValue, SalesforceLanguageCode } from 'bundles/common/utils/salesforceUrlBuilder';

import communityMappings from '../inSidedToSalesforceCommunityMappings.json';
import { getCachedMappingsInfo, refreshMappingsInBackgroundIfStale } from './helpArticlesMappingCache';
import type {
  InsidedToSalesforceMappingsCollection,
  PageTypeValue,
  RedirectInfo,
  RequestedZendeskLanguageCode,
  ZendeskHelpCenterSubdomainValue,
  ZendeskToSalesforcePageMappingsCollection,
} from './types';
import { pageTypes, zendeskHelpCenterSubdomains } from './types';

// IMPORTANT: Do not deprecate/delete this code, even though we have been off of Zendesk for some time now.
// This code is vital to our strategy of having permanent help article links.  We are using the Zendesk article URL format as our article permalinks.
// See https://go.dkandu.me/help-links-format

// TODO: (very low priority) Replace variable names and comments referring to Zendesk so that it's more clear that this is now more about our standard permanent help article URL format versus legacy Zendesk.

// Note: The 'id' language code was never actually supported by our Zendesk help centers, but going forward we may want to have /hc/id/ in article permalinks if there is a use case to specifically link to an Indonesian article.
const zendeskLanguageCodes: RequestedZendeskLanguageCode[] = ['en', 'ar', 'es', 'id'];
const validZendeskHomeUrlPaths = [
  '/',
  '/hc/',
  '/hc/en/',
  '/hc/en-us/',
  '/hc/ar/',
  '/hc/ar-sa/',
  '/hc/es/',
  '/hc/es-la/',
  '/hc/de', // buggy code in the coursera.org top right username menu was sending users to this URL and the ones below (even though we don't have these languages in ZD)
  '/hc/fr/',
  '/hc/ja/',
  '/hc/ko/',
  '/hc/pt/',
  '/hc/ru/',
  '/hc/zh/',
  '/hc/id/',
]; // we force a trailing slash and lowercase before checking a URL path against this list

const zendeskSubdomainToHelpCenter: Record<ZendeskHelpCenterSubdomainValue, HelpCenterValue> = {
  [zendeskHelpCenterSubdomains.PARTNER]: HelpCenters.PARTNER,
  [zendeskHelpCenterSubdomains.BUSINESS]: HelpCenters.ENTERPRISE,
  [zendeskHelpCenterSubdomains.LEARNER]: HelpCenters.LEARNER,
} as const;

// As soon as this module is initialized we can update the mappings cache (ahead of the first request)
refreshMappingsInBackgroundIfStale();

// Article mappings (ID -> Salesforce slug) are fetched from S3.  This mappings file can be updated with a Retool app at https://retool.coursera.org/apps/Help%20and%20Support/Help%20Article%20Mappings
const getHelpPageMappings = (): ZendeskToSalesforcePageMappingsCollection => {
  const cachedMappingsInfo = getCachedMappingsInfo();

  // Uncomment the lines below to verify the cache is working.  Also see the local dev note in helpArticlesMappingCache.ts
  // console.log('last updated', new Date(cachedMappingsInfo.lastUpdated).toISOString());
  // console.log('last updated sec ago', (new Date().getTime() - cachedMappingsInfo.lastUpdated) / 1000);

  refreshMappingsInBackgroundIfStale();

  // Return cached mappings right away, without waiting for the async mappings refresh to complete.  The mappings refresh that we kicked off above will be for the benefit of subsequent help article requests.
  return cachedMappingsInfo.mappings;
};

// inSided to SF mappings are now static, so they are simply imported from a file in this repo:
const insidedToSalesforceCommunityMappings = communityMappings as InsidedToSalesforceMappingsCollection;

const parseZendeskSubdomain = (url: string): ZendeskHelpCenterSubdomainValue | '' => {
  if (!url) {
    return '';
  }
  // For development and testing it can be useful not to use the prod domains. A "-test" subdomain uses the same article mapping lookup as prod.
  if (
    url.indexOf('https://learner.coursera.help') === 0 ||
    url.indexOf('https://learners.coursera.help') === 0 ||
    url.indexOf('https://learner-test.coursera.help') === 0
  ) {
    return zendeskHelpCenterSubdomains.LEARNER;
  }
  if (
    url.indexOf('https://partner.coursera.help') === 0 ||
    url.indexOf('https://partners.coursera.help') === 0 ||
    url.indexOf('https://partner-test.coursera.help') === 0
  ) {
    return zendeskHelpCenterSubdomains.PARTNER;
  }
  if (
    url.indexOf('https://business.coursera.help') === 0 ||
    url.indexOf('https://businesses.coursera.help') === 0 ||
    url.indexOf('https://business-test.coursera.help') === 0
  ) {
    return zendeskHelpCenterSubdomains.BUSINESS;
  }
  return '';
};

const parseLanguageCode = (zendeskUrl: string): RequestedZendeskLanguageCode => {
  if (!zendeskUrl) {
    return '';
  }

  for (const languageCode of zendeskLanguageCodes) {
    // Check for either e.g. /hc/en/ or /hc/en-us/.  Note that searching for just '/hc/ar' will give Arabic false positives on URLs like /hc/articles/123
    // We don't just parse /hc/*/ and return * because it's common for Zendesk URLs to omit the language code completely (e.g. https://learner.coursera.help/hc/articles/209818553)
    if (
      zendeskUrl.indexOf(`/hc/${languageCode}/`) > 0 ||
      zendeskUrl.indexOf(`/hc/${languageCode}-`) > 0 ||
      (String.prototype.endsWith && zendeskUrl.endsWith(`/hc/${languageCode}`)) // endsWith is not available in IE, but this should be running in SSR/node.js
    ) {
      return languageCode;
    }
  }

  return ''; // we don't default to English here.  For an unknown requested languge we should omit it, and it should pick up from their preferences.
};

const getSalesforceLanguageCode = (languageCode: RequestedZendeskLanguageCode): SalesforceLanguageCode | '' => {
  if (!languageCode) {
    return '';
  }
  if (languageCode === 'en') {
    return 'en_US';
  }
  if (languageCode === 'id') {
    return 'in'; // SF still uses the older language code for Indonesian
  }
  return languageCode; // other codes are 'es' and 'ar' which are the same in ZD and SF
};

const parseZendeskArticleId = (zendeskUrl: string): string => {
  const matchResults = zendeskUrl.match(/\/articles\/(\d+)/) || [];
  return matchResults[1] || '';
};

const parseZendeskCategoryId = (zendeskUrl: string): string => {
  const matchResults = zendeskUrl.match(/\/categories\/(\d+)/) || [];
  return matchResults[1] || '';
};

const parseZendeskSectionId = (zendeskUrl: string): string => {
  const matchResults = zendeskUrl.match(/\/sections\/(\d+)/) || [];
  return matchResults[1] || '';
};

export const parseInsidedThreadId = (insidedUrl: string): string => {
  const urlPath = new URI(insidedUrl).path();

  if (urlPath.indexOf('/groups/') === 0) {
    return ''; // URLs like https://coursera.community/groups/yale-behavior-change-74 only have a groupId and never a threadId
  }

  // inSided URLs look like https://coursera.community/gtc-news-announcements-17/join-the-coursera-global-translator-community-45
  const matchResults = urlPath.match(/^\/.+\/.+-(\d+$)/) || []; // if we have coursera.community/comm-45 we don't want it to pull 45 from that
  return matchResults[1] || '';
};

export const parseInsidedGroupId = (insidedUrl: string): string => {
  const urlPath = new URI(insidedUrl).path();

  // The groupId in this URL is '17': https://coursera.community/gtc-news-announcements-17/join-the-coursera-global-translator-community-45
  // The groupId in this URL is also '17': https://coursera.community/gtc-news-announcements-17
  // You could also have /groups/ at the beginning of the URL path: https://coursera.community/groups/yale-behavior-change-74 (groupId is '74')
  const matchResults = urlPath.match(/^\/(?:groups\/)?[^/]+-(\d+)/) || [];
  return matchResults[1] || '';
};

const getSalesforceArticleSlug = (
  zendeskSubdomain: ZendeskHelpCenterSubdomainValue,
  zendeskArticleId: string
): string => getHelpPageMappings()[zendeskSubdomain]?.article?.[zendeskArticleId] || '';

const getSalesforceUrlPathForCategory = (
  zendeskSubdomain: ZendeskHelpCenterSubdomainValue,
  zendeskCategoryId: string
): string => getHelpPageMappings()[zendeskSubdomain]?.category?.[zendeskCategoryId] || '';

const getSalesforceUrlPathForSection = (
  zendeskSubdomain: ZendeskHelpCenterSubdomainValue,
  zendeskSectionId: string
): string => getHelpPageMappings()[zendeskSubdomain]?.section?.[zendeskSectionId] || '';

const isZendeskHelpCenterHomeUrl = (zendeskUrl: string): boolean => {
  if (!zendeskUrl || !parseZendeskSubdomain(zendeskUrl)) {
    return false;
  }

  let path = new URI(zendeskUrl).path() || ''; // this does not throw on bad URL, and does not include querystring or anchor
  const pathEndsWithSlash = path.charAt(path.length - 1) === '/';
  if (!pathEndsWithSlash) {
    path += '/';
  }
  return validZendeskHomeUrlPaths.indexOf(path.toLowerCase()) >= 0;
};

const isZendeskWebformUrl = (zendeskUrl: string): boolean => {
  if (!zendeskUrl || !parseZendeskSubdomain(zendeskUrl)) {
    return false;
  }

  const path = new URI(zendeskUrl).path() || '';
  return /\/requests\/new\/?$/i.test(path); // path must end with /requests/new or /requests/new/
};

const getRedirectInfoForArticle = (
  zendeskSubdomain: ZendeskHelpCenterSubdomainValue,
  isAuthenticated: boolean,
  requestedLanguageCode: RequestedZendeskLanguageCode,
  zendeskArticleId: string,
  forceBypassSso = false
): RedirectInfo | null => {
  const salesforceArticleSlug = getSalesforceArticleSlug(zendeskSubdomain, zendeskArticleId);
  if (!salesforceArticleSlug) {
    return null;
  }

  const helpCenter = zendeskSubdomainToHelpCenter[zendeskSubdomain];
  const language = getSalesforceLanguageCode(requestedLanguageCode);

  // eslint-disable-next-line no-restricted-syntax
  const salesforceArticleUrl = buildSalesforceArticleUrl(
    helpCenter,
    isAuthenticated,
    language,
    salesforceArticleSlug,
    forceBypassSso
  );
  if (!salesforceArticleUrl) {
    return null;
  }

  return {
    location: salesforceArticleUrl,
    statusCode: 301, // this redirect will get cached on the browser side indefinitely.  developers should clear browser cache if making changes.
    helpCenter,
    pageType: pageTypes.ARTICLE,
    pageId: salesforceArticleSlug,
    language,
  };
};

const getRedirectInfoForSectionOrCategory = (
  zendeskSubdomain: ZendeskHelpCenterSubdomainValue,
  isAuthenticated: boolean,
  requestedLanguageCode: RequestedZendeskLanguageCode,
  zendeskSectionId: string,
  zendeskCategoryId: string,
  forceBypassSso = false
): RedirectInfo | null => {
  const pageType: PageTypeValue = zendeskCategoryId ? pageTypes.CATEGORY : pageTypes.SECTION;
  const salesforceUrlPath =
    pageType === pageTypes.CATEGORY
      ? getSalesforceUrlPathForCategory(zendeskSubdomain, zendeskCategoryId)
      : getSalesforceUrlPathForSection(zendeskSubdomain, zendeskSectionId);
  if (!salesforceUrlPath) {
    return null;
  }

  const helpCenter = zendeskSubdomainToHelpCenter[zendeskSubdomain];
  const language = getSalesforceLanguageCode(requestedLanguageCode);

  // eslint-disable-next-line no-restricted-syntax
  const salesforceUrl = buildSalesforceUrl(
    helpCenter,
    isAuthenticated,
    language,
    salesforceUrlPath,
    {},
    forceBypassSso
  );
  if (!salesforceUrl) {
    return null;
  }

  return {
    location: salesforceUrl,
    statusCode: 301, // this redirect will get cached on the browser side indefinitely.  developers should clear browser cache if making changes.
    helpCenter,
    language,
    pageType,
    pageId: salesforceUrlPath,
  };
};

/* eslint-disable no-restricted-syntax */ // disable warnings about hardcoded inSided URLs
const isInsidedCommunityUrl = (url: string): boolean =>
  url.indexOf('https://coursera.community') === 0 ||
  url.indexOf('https://www.coursera.community') === 0 ||
  url.indexOf('https://community-test.coursera.help') === 0;
/* eslint-enable no-restricted-syntax */

const getRedirectInfoForInsidedUrl = (insidedUrl: string): RedirectInfo => {
  const sfCommunityFallbackRedirectInfo: RedirectInfo = {
    location: buildCommunityFallbackUrl(),
    statusCode: 302,
    pageType: pageTypes.COMMUNITY_NOTFOUND,
  };

  const insidedThreadId = parseInsidedThreadId(insidedUrl);
  if (insidedThreadId) {
    const insidedToSalesforceMapping = insidedToSalesforceCommunityMappings.thread[insidedThreadId];
    if (insidedToSalesforceMapping?.sfId) {
      return {
        location: buildCommunityFeedUrl(insidedToSalesforceMapping.sfId, insidedToSalesforceMapping.type),
        statusCode: 301,
        pageType: pageTypes.COMMUNITY_THREAD,
        pageId: insidedToSalesforceMapping.sfId,
      };
    }
    // If we couldn't find a mapping for the thread, we'll try match the group/forum and at least take them to the right group in SF
  }

  const insidedGroupId = parseInsidedGroupId(insidedUrl);
  if (insidedGroupId) {
    const salesforceGroupId = insidedToSalesforceCommunityMappings.group[insidedGroupId]?.sfId;
    if (salesforceGroupId) {
      const isFallbackToGroup = !!insidedThreadId; // true if the user was trying to go to a thread and we fell back to a group
      return {
        location: buildCommunityGroupUrl(salesforceGroupId),
        statusCode: isFallbackToGroup ? 302 : 301, // temporary redirect enables us to fix the mapping for this thread, otherwise the redirect would be cached on the client side indefinitely
        pageType: isFallbackToGroup ? pageTypes.COMMUNITY_GROUP_FALLBACK : pageTypes.COMMUNITY_GROUP,
        pageId: salesforceGroupId,
      };
    }
  }

  return sfCommunityFallbackRedirectInfo;
};

export const getRedirectInfo = (
  sourceUrl: string,
  requestUrl: string,
  isAuthenticated: boolean,
  forceBypassSso = false
): RedirectInfo => {
  const requestUrlZendeskSubdomain = parseZendeskSubdomain(requestUrl);
  if (requestUrlZendeskSubdomain) {
    // We're currently handling a request on an intercepted Zendesk help center URL domain e.g. https://learner.coursera.help/hc/*.
    // Now we need to direct the user to www.coursera.org first so we can receive the CAUTH cookie:
    const isLocalDev = requestUrl.indexOf(':9443') > 0; // not using config.development for this check because we really only want this workaround for the local web server environment, not for any jest tests
    // eslint-disable-next-line no-restricted-syntax
    const newUrlBase = isLocalDev ? 'https://mock.dev-coursera.org:9443/' : config.url.base; // in development config.url.base resolves to site.dev-coursera.org instead of mock.dev-coursera.org.  this workaround enables the next URL in the redirect chain to also go to the local dev server.
    return {
      location: newUrlBase + 'hc/?sourceUrl=' + encodeURIComponent(requestUrl), // this will go back to the same route but with www.coursera.org as the URL domain, and the originally intended destination as a URL param
      statusCode: 301, // this redirect will get cached on the browser side indefinitely.  developers should clear browser cache if making changes.
    };
  }

  // If we're here, we need to analyze sourceUrl (the Zendesk URL that the user intended to go to) to figure out where to redirect.
  const requestedLanguageCode = parseLanguageCode(sourceUrl);
  const sfLanguageCode = getSalesforceLanguageCode(requestedLanguageCode);

  const fallbackPageRedirectInfo: RedirectInfo = {
    location: buildSalesforceFallbackUrl(
      HelpCenters.LEARNER,
      isAuthenticated,
      sfLanguageCode,
      sourceUrl,
      forceBypassSso
    ),
    statusCode: 302,
    helpCenter: HelpCenters.LEARNER,
    language: sfLanguageCode,
    pageType: pageTypes.NOTFOUND,
  };

  if (!sourceUrl) {
    return fallbackPageRedirectInfo;
  }

  const zendeskSubdomain = parseZendeskSubdomain(sourceUrl);
  if (!zendeskSubdomain) {
    if (isInsidedCommunityUrl(sourceUrl)) {
      // Note: Insided URLs will always redirect to Salesforce regardless of the epic check / shouldRedirectToSalesforce flag.  This is because it will go 100% live at once -- we can't have e.g. 10% of discussion happening in inSided while 90% is in SF.  Go-live will be completely controlled through DNS.
      return getRedirectInfoForInsidedUrl(sourceUrl);
    }
    return fallbackPageRedirectInfo;
  }

  const helpCenter = zendeskSubdomainToHelpCenter[zendeskSubdomain];

  if (isZendeskHelpCenterHomeUrl(sourceUrl)) {
    return {
      location: buildSalesforceHelpCenterHomeUrl(helpCenter, isAuthenticated, sfLanguageCode, forceBypassSso),
      statusCode: 301, // this redirect will get cached on the browser side indefinitely.  developers should clear browser cache if making changes.
      helpCenter,
      language: sfLanguageCode,
      pageType: pageTypes.HOME,
    };
  }

  if (isZendeskWebformUrl(sourceUrl)) {
    return {
      location: buildSalesforceWebformUrl(helpCenter, isAuthenticated, sfLanguageCode, forceBypassSso),
      statusCode: 301,
      helpCenter,
      language: sfLanguageCode,
      pageType: pageTypes.WEBFORM,
    };
  }

  const zendeskArticleId = parseZendeskArticleId(sourceUrl);
  if (zendeskArticleId) {
    return (
      getRedirectInfoForArticle(
        zendeskSubdomain,
        isAuthenticated,
        requestedLanguageCode,
        zendeskArticleId,
        forceBypassSso
      ) || fallbackPageRedirectInfo
    );
  }

  const zendeskSectionId = parseZendeskSectionId(sourceUrl);
  const zendeskCategoryId = parseZendeskCategoryId(sourceUrl);
  if (zendeskSectionId || zendeskCategoryId) {
    return (
      getRedirectInfoForSectionOrCategory(
        zendeskSubdomain,
        isAuthenticated,
        requestedLanguageCode,
        zendeskSectionId,
        zendeskCategoryId,
        forceBypassSso
      ) || fallbackPageRedirectInfo
    );
  }

  return fallbackPageRedirectInfo;
};
